import { useCallback, useEffect, useState } from 'react'

import { IconArchive, IconEdit } from '@procurify/ui'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { type RowCellMenuListItemProps } from '@webapp/components/DataTable'
import { toast } from '@webapp/components/Notification'
import { makeSelectUser } from '@webapp/core/stores/global'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { useArchiveCreditCardItemPresenter } from '@webapp/modules/accounts-payable/features/archive-credit-card-item/presenters/ArchiveCreditCardItemPresenter'
import { useCreditCardStatementDetailsPresenter } from '@webapp/modules/accounts-payable/features/get-credit-card-account/presenters/CreditCardStatementDetailsPresenter'
import { useCreditCardItemListPresenter } from '@webapp/modules/accounts-payable/features/list-credit-card-items/presenters/CreditCardItemListPresenter'
import {
  isRelatedItemsRejection,
  useMoveCreditCardItemsPresenter,
  type RelatedItemsPayload,
} from '@webapp/modules/accounts-payable/features/move-credit-card-items/presenters/MoveCreditCardItemsPresenter'
import { useUnarchiveCreditCardItemPresenter } from '@webapp/modules/accounts-payable/features/unarchive-credit-card-item/presenters/UnarchiveCreditCardItemPresenter'
import {
  AccountDetailsPageTabs,
  MatchPageTabs,
  type AccountDetailsPageTabEnum,
} from '@webapp/modules/accounts-payable/pages/types'
import messages from './messages'

export interface ItemModalState {
  mode: 'create' | 'edit' | 'view'
  item?: CreditCardItem
}

export interface IUseCreditCardAccountDetailsViewParams {
  accountId: number
  tab: AccountDetailsPageTabEnum
}

export const useCreditCardAccountDetailsView = ({
  accountId,
  tab,
}: IUseCreditCardAccountDetailsViewParams) => {
  const intl = useProcurifyIntl()
  const history = useHistory()
  const { url } = useAppRoutes()
  const currentUserId = useSelector(makeSelectUser()).id

  const archived = tab === AccountDetailsPageTabs.ARCHIVED

  // Presenter companion hook — never the Model hook (ADR-2); React Query
  // dedupes with the rendered CreditCardStatementDetailsPresenter.
  const { account, isLoading: isLoadingAccount } =
    useCreditCardStatementDetailsPresenter({ accountId })

  // Second presenter-hook call for the selection bridge (established
  // double-call pattern — shared list cache).
  const {
    data: { items },
  } = useCreditCardItemListPresenter({ accountId, archived })

  const [itemModal, setItemModal] = useState<Nullable<ItemModalState>>(null)
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false)
  const [moveMode, setMoveMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState<CreditCardItem[]>([])
  const [moveWarning, setMoveWarning] =
    useState<Nullable<RelatedItemsPayload>>(null)

  // Lock guard (BHV-007): a session locked by another user bounces back to
  // the accounts list with the "Session Locked" toast naming the user.
  useEffect(() => {
    if (account && account.isLockedByOther(currentUserId)) {
      toast.error({
        title: intl.formatMessage(messages.sessionLockedToastTitle),
        children: intl.formatMessage(messages.sessionLockedToastMessage, {
          name: account.session?.lockStatus.lockedByFullName,
        }),
      })
      history.replace(url('CreditCardReconciliationPage'))
    }
  }, [account, currentUserId, history, intl, url])

  const exitMoveMode = useCallback(() => {
    setMoveMode(false)
    setSelectedItems([])
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedItems([])
  }, [])

  const movePresenter = useMoveCreditCardItemsPresenter({
    selectedItems,
    sourceAccount: account,
    onDeselectAll: clearSelection,
    onMoved: exitMoveMode,
  })

  const { commands: archiveCommands } = useArchiveCreditCardItemPresenter()
  const { commands: unarchiveCommands } = useUnarchiveCreditCardItemPresenter()

  // Selection bridge (ADR-9): ids from the list feature resolve against the
  // current page + already-selected items.
  const handleSelectionChange = useCallback(
    (ids: number[]) => {
      setSelectedItems((previous) => {
        const pool = new Map(
          [...previous, ...items].map((item) => [item.id, item])
        )

        return ids.reduce<CreditCardItem[]>((selected, id) => {
          const item = pool.get(id)
          return item ? [...selected, item] : selected
        }, [])
      })
    },
    [items]
  )

  // Row menu slot composing the sibling command features (BHV-012/013/020).
  const renderItemRowMenu = useCallback(
    (item: CreditCardItem): RowCellMenuListItemProps[] => [
      {
        icon: <IconEdit />,
        text: intl.formatMessage(messages.editItemMenuItem),
        // Matched items open read-only (BHV-019).
        onClick: () =>
          setItemModal({ mode: item.isEditable ? 'edit' : 'view', item }),
      },
      archived
        ? {
            icon: <IconArchive />,
            text: intl.formatMessage(messages.unarchiveItemMenuItem),
            disabled: !item.isEditable,
            onClick: () => unarchiveCommands.unarchive(item),
          }
        : {
            icon: <IconArchive />,
            text: intl.formatMessage(messages.archiveItemMenuItem),
            disabled: !item.isEditable,
            onClick: () => archiveCommands.archive(item),
          },
    ],
    [archived, archiveCommands, intl, unarchiveCommands]
  )

  // Two-phase move (BHV-023): RELATED_ITEMS_FOUND rejections open the
  // FS-owned warning modal; other failures toast inside the feature.
  const handleMove = useCallback(async () => {
    try {
      await movePresenter.commands.move(selectedItems)
    } catch (error) {
      if (isRelatedItemsRejection(error)) {
        setMoveWarning(error.payload)
      }
    }
  }, [movePresenter.commands, selectedItems])

  const handleMoveConfirm = useCallback(async () => {
    const warning = moveWarning
    setMoveWarning(null)
    if (warning) {
      await movePresenter.commands.moveConfirmed(warning.allItemIds)
    }
  }, [movePresenter.commands, moveWarning])

  // Reconcile success navigates to the Unmatched Items route (BHV-028).
  const handleReconcileSuccess = useCallback(() => {
    setIsReconcileModalOpen(false)
    history.push(
      url('ReconciliationMatchPage', {
        id: String(accountId),
        tab: MatchPageTabs.UNMATCHED,
      })
    )
  }, [accountId, history, url])

  return {
    state: {
      account,
      isLoadingAccount,
      archived,
      itemModal,
      isReconcileModalOpen,
      moveMode,
      selectedItems,
      moveWarning,
      isMoving: movePresenter.states.isMoving,
      targetCard: movePresenter.targetCard,
      selectionTotal: movePresenter.selectionTotal,
    },
    methods: {
      openCreateItemModal: () => setItemModal({ mode: 'create' }),
      closeItemModal: () => setItemModal(null),
      openReconcileModal: () => setIsReconcileModalOpen(true),
      closeReconcileModal: () => setIsReconcileModalOpen(false),
      enterMoveMode: () => setMoveMode(true),
      exitMoveMode,
      clearSelection,
      handleSelectionChange,
      renderItemRowMenu,
      handleMove,
      handleMoveConfirm,
      dismissMoveWarning: () => setMoveWarning(null),
      handleReconcileSuccess,
      onTargetCardChange: movePresenter.onTargetCardChange,
    },
  }
}
